import { TableProps } from 'antd';
import { useCallback } from 'react';
import { useParams } from 'react-router';

import { useModalForm } from '../../../../foundation/hooks';
import { boldColValueRenderer } from '../../../../foundation/utils/boldColValueRenderer';
import {
  useGetVariantDetailQuery,
  useGetVariantTranscriptsQuery,
} from '../../../api/variantApi';
import { VARIANT_STAGE_TYPES } from '../../constants';
import { VariantTranscript } from '../../redux';

interface UseTranscriptListModalLogicProps {
  closeModal: () => void;
  onChangePrimaryTranscript: (record: VariantTranscript) => void;
}

/**
 * Custom hook that encapsulates all the business logic for TranscriptListModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useTranscriptListModalLogic = ({
  closeModal,
  onChangePrimaryTranscript,
}: UseTranscriptListModalLogicProps) => {
  const { isLoading, createModalOpts } = useModalForm({
    onClose: closeModal,
    stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  });

  const { vid } = useParams();
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: vid },
    { skip: !vid },
  );

  // Per the RTK Query tutorial: use query hooks to access cached data directly.
  // Use vid from URL params as the authoritative variant ID (same pattern as
  // all other RTK Query hooks in the workflow) instead of variant?.id from
  // Redux, which can be undefined during the async sync cycle.
  const { data: transcripts, isLoading: isTranscriptsLoading } =
    useGetVariantTranscriptsQuery({ variant_id: Number(vid) }, { skip: !vid });
  const dataSource = transcripts ?? [];

  const handleSelectTranscript = (record: VariantTranscript) => {
    if (record) {
      onChangePrimaryTranscript(record);
    }
  };

  const modalOpts = {
    ...createModalOpts(''),
  };

  const renderMatchTranscript = useCallback(
    (value: string, record: VariantTranscript) => {
      if (value !== record.transcript_external_id) {
        return boldColValueRenderer(value);
      }
    },
    [],
  );

  const getColumns = (
    renderThreshold: (
      value: boolean,
      record: VariantTranscript,
    ) => React.ReactNode,
  ): TableProps<VariantTranscript>['columns'] => [
    {
      title: 'Transcript',
      dataIndex: 'transcript_external_id',
      key: 'transcript_external_id',
      render: boldColValueRenderer,
    },
    {
      title: 'Match transcript',
      dataIndex: 'enst_id',
      key: 'enst_id',
      render: renderMatchTranscript,
    },
    {
      title: 'Selection',
      dataIndex: 'mane_transcript_type',
      key: 'mane_transcript_type',
      render: boldColValueRenderer,
    },
    {
      title: 'Consequence',
      dataIndex: 'consequence_terms_single_string',
      key: 'consequence_terms_single_string',
      render: boldColValueRenderer,
    },
    {
      title: 'cDNA',
      dataIndex: 'cdna',
      key: 'cdna',
    },
    {
      title: 'Set as primary transcript',
      dataIndex: 'is_current_primary_transcript',
      key: 'is_current_primary_transcript',
      render: renderThreshold,
    },
  ];

  return {
    isLoading,
    isTranscriptsLoading,
    variant,
    dataSource,
    modalOpts,
    handleSaveChanges: handleSelectTranscript,
    getColumns,
  };
};

export default useTranscriptListModalLogic;
